import { h, VNode, FunctionComponent, toChildArray } from "preact"; /** @jsx h */
import { useEffect, useRef, useState } from "preact/hooks";
import {computePosition, flip, shift, offset, arrow} from '@floating-ui/dom';


import { Session } from '@pyqml/model';
import { ROUTES } from "@pyqml/shared";

import { PaywallDivider } from '../components/PaywallDivider';

import { useAuth } from "../app/AuthProvider";
import Prism from 'prismjs';
import { useEnvironment } from "../app/Environment";
Prism.manual = true;

import { useCacheableQueryProvider } from "../app/CacheableQueryProvider";

export type IContentItemProps = {
	
    contentId?: string;
	text?: string;

    paywalled?: string;

    // pass through class name
	ptClassName?: string;
};

export const ContentItem = (props: IContentItemProps) => {
    
    const { baseUrl } = useEnvironment();
    const { cacheablePromise } = useCacheableQueryProvider();
    const contentElement = useRef<HTMLDivElement>(null);
    const { subscriptionLevel } = useAuth();
    const iSubscriptionLevel = subscriptionLevel ? Object.values(Session.SubscriptionLevel).indexOf(subscriptionLevel) : -1;
    
    const [text, setText] = useState<string>(props.text || "");
    
    useEffect(() => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(props.text || "", "text/html");
        const links = doc.getElementsByTagName("a");

        for (let i = 0; i < links.length; i++) {
            const refId = links[i].getAttribute("data-referenceid");
            const conceptSlug = links[i].getAttribute("data-concept-slug");

            if (refId && conceptSlug) {
                // Create the tooltip element
                const tooltipElement = document.createElement("div");
                tooltipElement.id = refId;
                tooltipElement.className = "calliopareference-tooltip";

                const arrowDiv = document.createElement("div");
                arrowDiv.id = `arrow-${refId}`;
                arrowDiv.className = "calliopareference-arrow";

                const contentDiv = document.createElement("div");
                contentDiv.id = `content-${refId}`;

                tooltipElement.appendChild(arrowDiv);
                tooltipElement.appendChild(contentDiv);

                doc.body.appendChild(tooltipElement);
            }
        }
        setText(doc.body.innerHTML);
        
    }, [props.text]);

    //console.log("ContentItem: ", iSubscriptionLevel, subscriptionLevel, props.paywalled);

    const insertDynamicContent = () => {
        
        window.renderMathInElement?.(contentElement.current, {
            delimiters: [
                {left: "$$", right: "$$", display: true},
                {left: "$", right: "$", display: false},
                
            ]
        })

        Prism.highlightAll();

        
        Object.values(Session.SubscriptionLevel).forEach((level, idx) => {

            //console.log("paywall level: ", level, idx, iSubscriptionLevel, props.paywalled)

            window.document.querySelectorAll(`.paywall-${level}`).forEach((element) => {
                if (element.children.length > 0) {
                    //console.log("element.children", element.children.length, iSubscriptionLevel >= idx, props.paywalled !== undefined)
                    // Element has children
                    // Add paywall logic here
                    if (props.paywalled/*iSubscriptionLevel >= idx*/) {
                        element.innerHTML = props.paywalled;

                        window.renderMathInElement?.(contentElement.current, {
                            delimiters: [
                                {left: "$$", right: "$$", display: true},
                                {left: "$", right: "$", display: false},
                                
                            ]
                        })
                        Prism.highlightAll();
                    }

                    
                } else {
                    // Element does not have children
                    // Add logic for handling elements without children
                    if (!props.paywalled /*iSubscriptionLevel < idx*/) {
                        element.innerHTML = `
                            <div class="dot-container">
                                <div class="dot-container"><div class="dots-right dots-color-${level}"></div></div>
                                <div class="dot-center"><span
                                        class="iconbox iconsmall rounded-circle text-white border-0 bg-${level}"
                                    >
                                        <i class="${level == 'researcher' ? 'fas fa-flask' : (level == 'practitioner' ? 'fas fa-user-astronaut' : 'fab fa-leanpub')}"></i>
                                    </span></div>
                                <div class="dot-container"><div class="dots-left dots-color-${level}"></div></div>
                            </div>
                            <div>
                            ${
                                subscriptionLevel && (
                                    `<p>The remainder of this post is available to PyQML ${level}s only. You are in ${level} mode. Upgrade your subscription.</p>
                                    <div class="btn-cta"><a href="${ROUTES.BLOG_LOGIN}" class="btn btn-dark">Upgrade</a></div>`
                                ) || (
                                    `<p>The remainder of this post is available to PyQML ${level}s only, please log in or create an account.</p>
                                    <div class="btn-cta"><a href="${ROUTES.BLOG_LOGIN}" class="btn btn-dark">Login</a></div>`
                                )
                            }
                                
                            </div>
                        `;
                    } else {
                        
                        element.innerHTML = props.paywalled;

                        window.renderMathInElement?.(contentElement.current, {
                            delimiters: [
                                {left: "$$", right: "$$", display: true},
                                {left: "$", right: "$", display: false},
                                
                            ]
                        })
                        Prism.highlightAll();
                    
                    }
                }
            });
        })

        const links = contentElement.current.getElementsByTagName("a");
                    
        for (let i=0; i < links.length; i++) {
            //console.log("link: ", links[i].getAttribute("href"), links[i].getAttribute("data-referenceid"));
            
            // see: https://floating-ui.com/docs/tutorial
            const refId = links[i].getAttribute("data-referenceid");
            const conceptSlug = links[i].getAttribute("data-concept-slug");


            if (refId && conceptSlug) {
                //console.log("refId: ", refId, conceptSlug);

                

                const tooltip = window.document.querySelector(`#${refId}`);
                const arrowElement = document.querySelector(`#arrow-${refId}`);
                const ttContentElement = window.document.querySelector(`#content-${refId}`);
                

                if (tooltip && arrowElement && ttContentElement) {

                    cacheablePromise?.({
                        method: "POST",
                        endpoint: ROUTES.CLOUDAPI_PUBLIC_GET_REFERENCE_TOOLTIP,
                        queryData: {
                            slug: conceptSlug
                        },
                        
                    }).then((response) => {
                        //
                        if (response?.data) {
                            //console.log("response: ", response.data);
                            
                            ttContentElement.innerHTML = `${
                                response.data?.wiki?.abstract
                            }${
                                `<a href="${ROUTES.BLOG_WIKI}/${response.data.wiki.slug}" class="calliopareference-mobile-only">Read more</a>`
                            }<hr class="calliopareference-line"/><ul>${
                                response.data.aspects?.reduce(
                                    (text, aspect) => `${text}<li><a href="${ROUTES.BLOG_WIKI}/${aspect.aspectSlug}">${aspect.aspectTitle}</a></li>`, ""
                                )}</ul>`;
                        }
                    }).catch((error) => {
                        console.error("Error fetching tooltip data: ", error);
                    });


                    //const tooltip = document.querySelector('#tooltip');
                    

                    function update() {
                        computePosition(links[i], tooltip, {
                            placement: 'top',
                            middleware: [offset(6), flip(), shift({padding: 5}), arrow({element: arrowElement})],
                        }).then(({x, y, placement, middlewareData}) => {
                            Object.assign(tooltip.style, {
                                left: `${x}px`,
                                top: `${y}px`,
                            });
                            const {x: arrowX, y: arrowY} = middlewareData.arrow;

                            const staticSide = {
                                top: 'bottom',
                                right: 'left',
                                bottom: 'top',
                                left: 'right',
                            }[placement.split('-')[0]];
                            
                            Object.assign(arrowElement.style, {
                                left: arrowX != null ? `${arrowX}px` : '',
                                top: arrowY != null ? `${arrowY}px` : '',
                                right: '',
                                bottom: '',
                                [staticSide]: '-4px',
                            });

                        });
                    }

                                    
                    function showTooltip() {
                        tooltip.style.display = 'inline-block';
                        update();
                    }
                    
                    function hideTooltip() {
                        tooltip.style.display = '';
                    }

                    
                    
                    
                    if (window.innerWidth > 767.98) {

                        const handleClickOutside = (event: MouseEvent) => {
                            if (!tooltip.contains(event.target as Node) && !links[i].contains(event.target as Node)) {
                                hideTooltip();
                                document.removeEventListener('mousedown', handleClickOutside);
                            }
                        };
    
                        document.addEventListener('mousedown', handleClickOutside);

                        let hideTimeout: NodeJS.Timeout;
                        
                        [
                            ['mouseenter', showTooltip],
                            ['focus', showTooltip],
                            ['blur', hideTooltip],
                            //['mouseleave', hideTooltip]
                        ].forEach(([event, listener]) => {
                            links[i].addEventListener(event, listener);
                        });

                        
                        tooltip.addEventListener('mouseenter', () => {
                            clearTimeout(hideTimeout);
                        });

                        links[i].addEventListener('mouseleave', () => {
                            hideTimeout = setTimeout(hideTooltip, 400);
                        });

                        tooltip.addEventListener('mouseleave', () => {
                            hideTimeout = setTimeout(hideTooltip, 400);
                        });
                    } else {
                        console.log("mobile");

                        const addClickListener = (link) => (e: MouseEvent) => {
                            e.preventDefault();

                            link.addEventListener('click', (e) => {
                                e.preventDefault();
                                link.addEventListener('click', addClickListener(link));
                                hideTooltip();
                            });

                            showTooltip();

                        };


                        // Mobile
                        if (links[i]) {
                            links[i].setAttribute('href', 'javascript:void(0);'); // Set href to a JavaScript no-op
                            links[i].addEventListener('click', addClickListener(links[i]));
                            
                        }

                        if (tooltip) {
                            tooltip.addEventListener('click', (e) => {
                                e.preventDefault();
                                links[i].addEventListener('click', addClickListener(links[i]));
                                hideTooltip();
                            });
                        }
                    }
                }
            }
                

            const isExternal =  links[i].getAttribute("href")?.startsWith("http") || links[i].getAttribute("href")?.startsWith("mailto");

            if (!refId) {
                if (!isExternal) {
                    links[i].setAttribute("href",`${baseUrl}${links[i].getAttribute("href")?.startsWith("/") ? "" : "/"}${links[i].getAttribute("href")}`);
                } else if (!(links[i].getAttribute("href")?.startsWith("https://www.pyqml.com"))) {
                    links[i].setAttribute("target","_blank");
                }
            }
            
        }



    };
    
    useEffect(() => {
        // when the component takes longer to mount, render the math when the children change
        const observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                insertDynamicContent();

            });    
        });

        if (contentElement.current) {
               
            // when the component mounts fast, render the math right away (in abstracts)
            insertDynamicContent();


            // configuration of the observer:
            var config = { attributes: true, childList: true, characterData: true };

            // pass in the target node, as well as the observer options
            observer.observe(contentElement.current, config);

        }
        return () => {
            observer.disconnect();
        }

        

      }, [contentElement.current, props.paywalled]);
        
    return (
        <div className={props.ptClassName}
            ref={contentElement}
            id={props.contentId}
            dangerouslySetInnerHTML={{__html: text}} 
        ></div>		
	
	);
}
